import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { Editor, Element, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { ELEMENT_PARAGRAPH } from '../../../../../slatePlugins';
import { SlatePlugin } from '../../../../../slatePlugins/slatePlugins.types';
import { ELEMENT_VOCAB_IDEA_TITLE } from './VocabIdeaTitlePlugin.types';
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';

const VocabIdeaTitleElement = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/VocabIdeaTitleElement').then((mod) => mod.VocabIdeaTitleElement),
));
const VocabIdeaParagraphElement = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/VocabIdeaParagraphElement').then((mod) => mod.VocabIdeaParagraphElement),
));

export const generateVocabIdeaTitleNode = (text = '') => {
  return {
    id: nanoid(),
    type: ELEMENT_VOCAB_IDEA_TITLE,
    children: [getEmptyParagraphNode(text)],
  };
};

interface VocabIdeaTitlePluginOptions {
  isReadOnly?: boolean;
  enabled?: boolean;
  audio?: any;
  paragraphIndex?: number;
}

export const VocabIdeaTitlePlugin = (options: VocabIdeaTitlePluginOptions = {}): SlatePlugin => {
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;

      if (element?.type === ELEMENT_VOCAB_IDEA_TITLE) {
        return <VocabIdeaTitleElement {...renderProps} {...options} />;
      }
      if (element?.type === ELEMENT_PARAGRAPH) {
        return <VocabIdeaParagraphElement {...renderProps} {...options} />;
      }

      return undefined;
    },
  };
};

export const withVocabIdeaTitleEditorChain =
  ({ normalizeEnabled }) =>
  <T extends ReactEditor>(editor: T) => {
    const { insertBreak, normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];
      if (normalizeEnabled && path.length === 1) {
        if (path[0] === 0 && (node as any)?.type !== ELEMENT_VOCAB_IDEA_TITLE) {
          Transforms.insertNodes(editor, generateVocabIdeaTitleNode(), {
            at: path,
          });
          return;
        }
        let haveSecondNode = false;
        try {
          const [secondNode] = Editor.nodes(editor, { at: [1] });
          haveSecondNode = Boolean(secondNode);
          // eslint-disable-next-line no-empty
        } catch (ex) {}

        if (!haveSecondNode) {
          Transforms.insertNodes(editor, getEmptyParagraphNode(), {
            at: [1],
          });
          return;
        }
      }
      normalizeNode(entry);
    };

    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [foundNode] = Editor.nodes(editor, {
          match: (n: any) =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === ELEMENT_VOCAB_IDEA_TITLE,
        });

        if (foundNode) {
          editor.insertText('\n');
          return;
        }
      }

      insertBreak();
    };

    return editor;
  };
